import getCategoryOrder from "components/financials/getCategoryOrder";
import type { IIndicatorFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/IndicatorFieldsFragment.generated";
import ArbitraryIndicatorButton from "components/financials/modern/indicators/ArbitraryIndicatorButton";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import classNames from "components/ui/classNames";
import { LayoutGroup } from "framer-motion";
import { useAmplitude } from "lib/amplitude/Amplitude";
import usePagination from "lib/hooks/usePagination";
import orderBy from "lodash/orderBy";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { startTransition, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface PagedIndicatorsGroupProps {
  mode?: "TOGGLE";
  limit?: number;
  searchQuery?: string;
  indicators: IIndicatorFieldsFragment[];
  selectedIndicators: IIndicatorFieldsFragment[];
  onToggleIndicator: (indicator: IIndicatorFieldsFragment, isSelected: boolean) => void;
  onSelectIndicator: (indicator: IIndicatorFieldsFragment) => void;
}
export default function PagedIndicatorsGroup({
  mode,
  limit,
  searchQuery,
  indicators,
  selectedIndicators,
  onToggleIndicator,
  onSelectIndicator,
}: PagedIndicatorsGroupProps) {
  const intl = useIntl();
  const amplitude = useAmplitude();
  const pagination = usePagination({
    items: indicators,
    itemsPerPage: 3,
    mode: "slice",
  });
  useEffect(() => {
    if (pagination.currentPage !== 0) {
      startTransition(() => {
        pagination.reset();
      });
    }
  }, [searchQuery || ""]);

  const displayIndicators = orderBy(
    pagination.currentItems.map((node) => {
      return {
        node,
        isSelected: selectedIndicators.some(({ objectId }) => objectId === node.objectId),
        category: getCategoryOrder(node.category),
      };
    }),
    ["isSelected", "category"],
    ["desc", "asc"],
  ).map(({ node }) => node);

  const totalPages = pagination.pages.length;
  const isOverLimit = limit !== undefined && selectedIndicators.length >= limit;

  return (
    <LayoutGroup>
      <div className="space-y-2">
        <div
          aria-label={intl.formatMessage(
            { defaultMessage: "Página {currentPage}", id: "zTp6qa" },
            { currentPage: pagination.currentPage },
          )}
          className={classNames("flex flex-1 items-center space-x-2", {
            hidden: indicators.length === 0,
          })}
        >
          <ScrollArrowButton disabled={!pagination.hasNextPage} onClick={() => pagination.goToNextPage()}>
            <ChevronDownIcon strokeWidth={2} />
          </ScrollArrowButton>
          <ScrollArrowButton disabled={!pagination.hasPreviousPage} onClick={() => pagination.goToPreviousPage()}>
            <ChevronUpIcon strokeWidth={2} />
          </ScrollArrowButton>
        </div>

        {isOverLimit && (
          <p
            className={classNames(
              "text-xs font-medium italic leading-5 tracking-wide text-slate-700",
              "rounded-xl",
              "bg-orange-50/60 px-3 py-1 text-orange-600 dark:bg-orange-600/10 dark:text-orange-300",
            )}
          >
            <FormattedMessage
              defaultMessage="Você atingiu o limite de {limit, plural, one {# indicator} other {# indicadores}}..."
              id="Pp9/hN"
              values={{ limit }}
            />
          </p>
        )}

        <div className="space-y-1">
          {displayIndicators.map((indicator) => {
            const isSelected = selectedIndicators.some(({ objectId }) => objectId === indicator.objectId);

            return (
              <ArbitraryIndicatorButton
                key={indicator.objectId}
                searchQuery={searchQuery}
                indicator={indicator}
                isSelected={isSelected}
                displayIndicatorsCount={displayIndicators.length}
                disabled={isOverLimit || !indicator.viewerCanSee}
                onClick={() => {
                  if (!isSelected) {
                    amplitude.logEvent("Selected arbitrary indicator", {
                      objectId: indicator.objectId,
                      name: getIndicatorDocumentation(intl, indicator).name,
                      category: indicator.category,
                    });
                  }

                  if (mode === "TOGGLE") {
                    onToggleIndicator(indicator, isSelected);
                    return;
                  }
                  onSelectIndicator(indicator);
                }}
              />
            );
          })}

          {indicators.length === 0 && (
            <p
              className={classNames(
                "m-0 rounded-lg border border-slate-200 bg-slate-50 p-2 text-center text-sm font-medium text-slate-600",
                "dark:border-transparent dark:bg-transparent dark:text-slate-400",
                "italic tracking-wide",
              )}
            >
              <FormattedMessage defaultMessage="Nenhum indicador encontrado." id="BE9tbp" />
            </p>
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center rounded-lg bg-slate-50 py-2 text-xs font-bold tracking-wider dark:bg-slate-950/30 dark:text-white">
            {pagination.currentPage} / {totalPages}
          </div>
        )}
      </div>
    </LayoutGroup>
  );
}

function ScrollArrowButton({
  className,
  children,
  ...props
}: React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>) {
  return (
    <button
      {...props}
      type="button"
      className={classNames(
        "flex flex-1 justify-center rounded-xl bg-slate-100 p-2",
        "dark:bg-slate-700 dark:text-slate-200",
        "disabled:cursor-not-allowed disabled:text-slate-300 dark:disabled:opacity-50",
        className,
      )}
    >
      {children}
    </button>
  );
}
